/**
 * Sortable column headers.
 *
 * Each header is a real `<button>` inside its `<th>`, and the `<th>` carries
 * `aria-sort`, so the sort state is announced rather than inferred from a
 * glyph. The scroll container is focusable so a keyboard user can scroll a
 * wide table without a pointer.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
  toggle: (key: K) => void;
  ariaSort: (key: K) => 'ascending' | 'descending' | 'none';
}

export function useSort<K extends string>(
  initialKey: K,
  initialDirection: SortDirection = 'asc',
): SortState<K> {
  const [key, setKey] = useState<K>(initialKey);
  const [direction, setDirection] = useState<SortDirection>(initialDirection);

  const toggle = useCallback(
    (next: K) => {
      setKey((current) => {
        if (current === next) {
          setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
          return current;
        }
        setDirection('asc');
        return next;
      });
    },
    [],
  );

  const ariaSort = useCallback(
    (candidate: K): 'ascending' | 'descending' | 'none' => {
      if (candidate !== key) return 'none';
      return direction === 'asc' ? 'ascending' : 'descending';
    },
    [key, direction],
  );

  return { key, direction, toggle, ariaSort };
}

/** Nulls sort last in both directions: "unknown" is not "zero". */
export function compareValues(a: unknown, b: unknown): number {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return Number(a) - Number(b);
  }
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function useSortedRows<T, K extends string>(
  rows: readonly T[],
  sort: SortState<K>,
  accessor: (row: T, key: K) => unknown,
): T[] {
  return useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const result = compareValues(accessor(a, sort.key), accessor(b, sort.key));
      // Nulls stay at the bottom regardless of direction.
      const aMissing = accessor(a, sort.key) == null;
      const bMissing = accessor(b, sort.key) == null;
      if (aMissing !== bMissing) return result;
      return sort.direction === 'asc' ? result : -result;
    });
    return copy;
    // `accessor` is a stable module-level function at every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort.key, sort.direction]);
}

export function SortHeader<K extends string>({
  columnKey,
  sort,
  children,
  numeric = false,
}: {
  columnKey: K;
  sort: SortState<K>;
  children: ReactNode;
  numeric?: boolean;
}) {
  const active = sort.key === columnKey;
  return (
    <th
      scope="col"
      className={numeric ? 'numeric' : undefined}
      aria-sort={sort.ariaSort(columnKey)}
    >
      <button
        type="button"
        className="sort-button"
        onClick={() => sort.toggle(columnKey)}
      >
        <span>{children}</span>
        <span className="sort-button__arrow" data-inactive={!active} aria-hidden="true">
          {active && sort.direction === 'desc' ? '▼' : '▲'}
        </span>
      </button>
    </th>
  );
}

/** The scroll box. Wide tables scroll here, never on the page. */
export function TableScroll({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label={label}>
      {children}
    </div>
  );
}
